/**
 * Export Conversation Button Component
 * Allows users to export conversation to knowledge base for AI learning
 */
import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import config from '../config';

const API_BASE_URL = config.apiUrl;

export default function ExportConversationButton({ sessionId, messageCount }) {
  const { t } = useTranslation(['common', 'chat']);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleOpen = () => {
    setOpen(true);
    setResult(null);
  };

  const handleClose = () => {
    setOpen(false);
    setResult(null);
  };

  const handleExport = async () => {
    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${API_BASE_URL}/conversations/export`,
        { session_id: sessionId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setResult({
        success: true,
        message: response.data?.data?.message || 'Export thành công!',
        exportId: response.data?.data?.export_id,
        messageCount: response.data?.data?.message_count,
      });
    } catch (error) {
      console.error('Export error:', error);
      let errorMessage = 'Có lỗi xảy ra khi export conversation.';
      
      if (error.response?.status === 409) {
        errorMessage = 'Cuộc hội thoại này đã được export trước đó.';
      } else if (error.response?.status === 400) {
        // Handle validation errors (array) and normal errors (string)
        const detail = error.response?.data?.detail;
        if (Array.isArray(detail) && detail.length > 0) {
          // Pydantic validation error
          const firstError = detail[0];
          errorMessage = `Lỗi xác thực: ${firstError.msg || 'Dữ liệu không hợp lệ'}`;
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = 'Cuộc hội thoại cần có ít nhất 2 tin nhắn.';
        }
      } else if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail);
      } else if (error.message) {
        errorMessage = `Lỗi kết nối: ${error.message}`;
      }

      setResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  // Don't show button if not enough messages
  if (!sessionId || messageCount < 2) {
    return null;
  }

  return (
    <>
      <Tooltip title="Lưu vào Knowledge Base để AI học hỏi" arrow>
        <IconButton
          onClick={handleOpen}
          sx={{
            color: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.light',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s',
          }}
        >
          <BookmarkAddIcon />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <BookmarkAddIcon color="primary" />
            <Typography variant="h6">Lưu Hội Thoại vào Knowledge Base</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {!result && (
            <>
              <DialogContentText>
                Bạn có muốn lưu cuộc hội thoại này vào knowledge base không?
              </DialogContentText>
              
              <Box mt={2} p={2} bgcolor="info.light" borderRadius={1}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Lợi ích:</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, mt: 1 }}>
                  <li>AI sẽ học từ cuộc hội thoại này</li>
                  <li>Câu trả lời tương lai sẽ nhất quán hơn</li>
                  <li>Chia sẻ kiến thức trong phòng ban</li>
                  <li>Số tin nhắn: {messageCount}</li>
                </Typography>
              </Box>
            </>
          )}

          {result && (
            <Box mt={2}>
              {result.success ? (
                <Alert 
                  severity="success" 
                  icon={<CheckCircleIcon />}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    <strong>{result.message}</strong>
                  </Typography>
                  <Typography variant="caption" display="block" mt={1}>
                    Export ID: {result.exportId}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Số tin nhắn: {result.messageCount}
                  </Typography>
                  <Typography variant="caption" display="block" mt={1} color="text.secondary">
                    Conversation đang được xử lý và sẽ có sẵn trong knowledge base sau vài phút.
                  </Typography>
                </Alert>
              ) : (
                <Alert 
                  severity="error" 
                  icon={<ErrorIcon />}
                >
                  <Typography variant="body2">
                    {result.message}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!result && (
            <>
              <Button onClick={handleClose} disabled={loading}>
                Hủy
              </Button>
              <Button
                onClick={handleExport}
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <BookmarkAddIcon />}
              >
                {loading ? 'Đang Export...' : 'Xác Nhận Export'}
              </Button>
            </>
          )}
          
          {result && (
            <Button onClick={handleClose} variant="contained">
              Đóng
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

